import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { ConsentPreferences } from '@/hooks/use-consent'

interface ConsentBannerProps {
  onAcceptAll: () => void
  onRejectAll: () => void
  onSave: (prefs: ConsentPreferences) => void
}

const CATEGORIES = [
  {
    id: 'essential' as const,
    name: 'Essential',
    isEssential: true,
    description:
      'Your authentication session, Drops balance, and local app state. Required for SWIM to function — cannot be disabled.',
  },
  {
    id: 'analytics' as const,
    name: 'Analytics',
    isEssential: false,
    description:
      'PostHog collects data about which offers you view, how Drops are purchased, and where errors occur. Events are linked to your account when you are signed in. Helps us understand what works and what to fix. Data processed by PostHog Inc (US). GDPR-compliant via Standard Contractual Clauses.',
  },
  {
    id: 'errorTracking' as const,
    name: 'Error Tracking',
    isEssential: false,
    description:
      'Sentry captures JavaScript errors and browser diagnostics when something breaks on the platform — so our team can diagnose and fix it. May include the page URL, browser version, and error stack traces. No personally identifiable information is intentionally collected. Data processed by Functional Software Inc (US).',
  },
]

export function ConsentBanner({ onAcceptAll, onRejectAll, onSave }: ConsentBannerProps) {
  const { t } = useTranslation('common')
  const [showCustomize, setShowCustomize] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [errorTracking, setErrorTracking] = useState(false)

  function handleSave() {
    onSave({ analytics, errorTracking })
    setShowCustomize(false)
  }

  function handleRejectAll() {
    setAnalytics(false)
    setErrorTracking(false)
    onRejectAll()
  }

  return (
    <>
      {/* Banner — mobile: full-width sheet snapped to the bottom edge.
          Desktop (sm+): floating bottom-right card. The old `w-full right-4`
          combo pushed the card off the LEFT edge on viewports narrower than
          ~416px — w-full is 100vw, anchored 16px from the right.
          Solid light-steel background (slate-200), no blur/transparency —
          the previous bg-slate-900/98 + backdrop-blur-md read as illegible
          "frosted glass" on desktop. */}
      <div className="fixed bottom-0 inset-x-0 sm:bottom-4 sm:left-auto sm:right-4 z-50 w-full sm:max-w-[400px]" role="region" aria-label={t('privacy.preferences')}>
        <div className="rounded-t-2xl rounded-b-none sm:rounded-2xl border border-slate-300 bg-slate-200 shadow-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-5">
          <div className="mb-3">
            <div className="flex items-center gap-3 mb-2">
              <div className="shrink-0 rounded-lg bg-ocean-500/15 p-1.5">
                <Shield className="h-4 w-4 text-ocean-600" aria-hidden="true" />
              </div>
              <p className="font-sans text-sm font-semibold text-slate-800">{t('privacy.yourData')}</p>
            </div>
            <p className="font-sans text-xs text-slate-600 leading-relaxed">
              {t('privacy.description')}{' '}
              <a
                href="/privacy"
                className="text-ocean-600 hover:text-ocean-700 underline underline-offset-2 transition-colors"
              >
                {t('privacy.privacyPolicy')}
              </a>
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={onAcceptAll}
              size="sm"
              className="flex-1 h-8 rounded-full bg-ocean-500 hover:bg-ocean-600 text-white text-xs font-medium"
            >
              {t('privacy.acceptAll')}
            </Button>
            <Button
              onClick={() => setShowCustomize(true)}
              size="sm"
              variant="outline"
              className="flex-1 h-8 rounded-full border-slate-400 bg-transparent text-slate-700 hover:bg-slate-300 hover:text-slate-900 text-xs font-medium"
            >
              {t('privacy.customize')}
            </Button>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="font-sans text-[10px] text-slate-500">
              {t('privacy.copyright')}
            </p>
            <a
              href="/privacy#your-rights"
              className="font-sans text-[10px] text-ocean-700 hover:text-ocean-800 transition-colors underline underline-offset-2"
            >
              {t('privacy.doNotSell')}
            </a>
          </div>
        </div>
      </div>

      {/* Customize dialog */}
      <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
        <DialogContent className="sm:max-w-[480px] bg-slate-900 border-white/10 p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
            <DialogTitle className="font-sans text-base font-semibold text-slate-100">
              {t('privacy.preferences')}
            </DialogTitle>
            <DialogDescription className="font-sans text-xs text-slate-400 leading-relaxed">
              {t('privacy.essential')}{' '}
              <a
                href="/privacy"
                className="text-ocean-400 hover:text-ocean-300 underline underline-offset-2 transition-colors"
              >
                {t('privacy.viewPolicy')}
              </a>
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5 space-y-3 max-h-[50vh] overflow-y-auto">
            {CATEGORIES.map((cat) => {
              const isOn = cat.isEssential
                ? true
                : cat.id === 'analytics'
                  ? analytics
                  : errorTracking

              const toggle =
                cat.id === 'analytics'
                  ? setAnalytics
                  : cat.id === 'errorTracking'
                    ? setErrorTracking
                    : null

              return (
                <div
                  key={cat.id}
                  className={cn(
                    'rounded-xl border p-4 transition-colors',
                    isOn && !cat.isEssential
                      ? 'border-ocean-500/30 bg-ocean-500/5'
                      : 'border-white/8 bg-white/[0.02]',
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`consent-${cat.id}`}
                        className="font-sans text-sm font-semibold text-slate-200 cursor-pointer"
                      >
                        {cat.name}
                      </Label>
                      {cat.isEssential && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-800 text-slate-500 cursor-default select-none">
                                {t('privacy.required')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">{t('privacy.requiredDescription')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <Switch
                      id={`consent-${cat.id}`}
                      checked={isOn}
                      onCheckedChange={toggle ? (v: boolean) => toggle(v) : undefined}
                      disabled={cat.isEssential}
                      aria-label={`${cat.name} ${isOn ? 'enabled' : 'disabled'}`}
                    />
                  </div>
                  <p className="font-sans text-xs text-slate-500 leading-relaxed">
                    {cat.description}
                  </p>
                </div>
              )
            })}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-white/10 bg-white/[0.02]">
            <div className="flex w-full items-center justify-between gap-3">
              <p className="font-sans text-[10px] text-slate-500 hidden sm:block shrink-0">
                {t('privacy.company')}
              </p>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="border-white/15 bg-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200 text-xs"
                >
                  {t('privacy.rejectAll')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className="bg-ocean-500 hover:bg-ocean-600 text-white text-xs"
                >
                  {t('privacy.savePreferences')}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
